import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Plus, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MyApplications } from "@/components/apply/MyApplications";
import { api } from "@/lib/api";
import { usePreviewGuard } from "@/lib/preview";
import { money, pct, fmtDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";

const Row = ({ k, v, testid }) => (
  <div className="flex items-baseline justify-between gap-3 py-1.5 text-sm">
    <span className="text-muted-foreground">{k}</span>
    <span className="num text-right font-medium" data-testid={testid}>{v}</span>
  </div>
);

const LoanCard = ({ l, i, onPay }) => (
  <div data-testid={`portal-loan-card-${l.loan_number}`} style={{ animationDelay: `${i * 70}ms` }} className="card-lift fade-up flex flex-col rounded-lg border bg-card p-6">
    <Link to={`/portal/loans/${l.id}`} className="block" data-testid={`portal-loan-link-${l.loan_number}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="mono text-xs text-muted-foreground">{l.loan_number}</div>
          <div className="mt-1 font-display text-xl font-semibold">{l.interest_type === "simple" ? "Simple-interest loan" : "Amortized loan"}</div>
          <div className="text-xs text-muted-foreground">{money(l.principal_cents)} · {pct(l.apr)} · {l.term_months} mo</div>
        </div>
        <StatusBadge status={l.status} testid={`portal-loan-status-${l.loan_number}`} />
      </div>
      <div className="mt-5 border-t pt-3">
        <Row k="Principal balance" v={money(l.principal_balance_cents)} testid={`portal-loan-balance-${l.loan_number}`} />
        <Row k="Next due" v={l.next_due ? `${money(l.next_due.amount_cents)} · ${fmtDate(l.next_due.due_date)}` : "—"} testid={`portal-loan-next-due-${l.loan_number}`} />
        <Row k="Payoff amount" v={money(l.payoff_cents)} />
        {l.past_due_cents > 0 && <Row k="Past due" v={money(l.past_due_cents)} />}
      </div>
    </Link>
    <div className="mt-auto flex items-center justify-between gap-3 pt-4">
      <Link to={`/portal/loans/${l.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary">View details <ArrowRight className="h-4 w-4" /></Link>
      {l.status !== "paid_off" && (
        <Button size="sm" onClick={() => onPay(l)} data-testid={`pay-now-${l.loan_number}`}><CreditCard /> Pay now</Button>
      )}
    </div>
  </div>
);

export default function PortalHome() {
  const [items, setItems] = useState(null);
  const { guard } = usePreviewGuard();
  const navigate = useNavigate();
  useEffect(() => { api.get("/portal/loans").then((r) => setItems(r.data.items)); }, []);
  const total = (items || []).reduce((s, l) => s + l.payoff_cents, 0);

  return (
    <div data-testid="portal-home">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">My loans</h1>
        <Button asChild data-testid="portal-apply-button"><Link to="/portal/apply" onClick={guard()}><Plus /> Apply for a loan</Link></Button>
      </div>
      <div className="gold-rule mt-4 w-24" />
      {items && (
        <div className="fade-up mt-8 rounded-lg border bg-card p-6" data-testid="portal-total-card">
          <div className="eyebrow">Total payoff across loans</div>
          <div className="num mt-2 font-display text-4xl font-semibold" data-testid="portal-total-payoff">{money(total)}</div>
          <div className="mt-1 text-xs text-muted-foreground">{items.length} loan{items.length === 1 ? "" : "s"} · balances as of today (US/Eastern)</div>
        </div>
      )}
      <div className="mt-6 grid gap-5 md:grid-cols-2">
        {items?.map((l, i) => <LoanCard key={l.id} l={l} i={i} onPay={guard(() => navigate(`/portal/loans/${l.id}/pay`))} />)}
        {items && !items.length && <div className="text-sm text-muted-foreground" data-testid="portal-no-loans">You have no loans yet.</div>}
      </div>
      <MyApplications />
    </div>
  );
}
